import { useState, useEffect } from 'react'
import { useUser } from '@clerk/clerk-react'
import { X, FolderOpen, Trash2, Upload, Loader, Plus, Clock, Eye } from 'lucide-react'
import { useStore } from '../store/useStore'
import { showToast } from './ui/Toast'

export default function MySketchesPanel({ onClose }) {
  const { user, isSignedIn } = useUser()
  const { setFiles, setSketchName, setP5Version } = useStore()
  
  const [sketches, setSketches] = useState([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [deleting, setDeleting] = useState(null)
  
  useEffect(() => {
    if (isSignedIn && user) {
      fetchSketches()
    }
  }, [isSignedIn, user])
  
  const fetchSketches = async () => {
    if (!user) return
    
    setLoading(true)
    try {
      const response = await fetch(`/api/user/sketches?userId=${user.id}`)
      const data = await response.json()
      setSketches(data.sketches || [])
    } catch (error) {
      console.error('[MySketchesPanel] Error fetching:', error)
      showToast('Failed to load sketches', 'error')
    } finally {
      setLoading(false)
    }
  }
  
  const handleLoadSketch = async (sketch) => {
    try {
      const response = await fetch(`/api/user/sketches?id=${sketch.id}&userId=${user.id}`)
      const data = await response.json()
      
      if (data.sketch) {
        // Convert to store format
        const files = data.sketch.files.map(f => ({
          name: f.name,
          content: f.content,
          type: f.type
        }))
        
        setFiles(files)
        setSketchName(data.sketch.name)
        if (data.sketch.p5Version) {
          setP5Version(data.sketch.p5Version)
        }
        
        showToast(`Loaded "${sketch.name}"`, 'success')
        onClose()
      }
    } catch (error) {
      console.error('[MySketchesPanel] Error loading:', error)
      showToast('Failed to load sketch', 'error')
    }
  }
  
  const handleSaveCurrentSketch = async () => {
    if (!user) return
    
    const { files, sketchName, p5Version } = useStore.getState()
    
    setSaving(true)
    try {
      const response = await fetch('/api/user/sketches', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId: user.id,
          name: sketchName,
          files: files.map(f => ({
            name: f.name,
            content: f.content,
            type: f.type
          })),
          p5Version
        })
      })
      
      const data = await response.json()
      
      if (data.success) {
        showToast('Sketch saved!', 'success')
        fetchSketches()
      } else {
        throw new Error(data.error)
      }
    } catch (error) {
      console.error('[MySketchesPanel] Error saving:', error)
      showToast('Failed to save sketch', 'error')
    } finally {
      setSaving(false)
    }
  }
  
  const handleDeleteSketch = async (sketch) => {
    if (!confirm(`Delete "${sketch.name}"? This cannot be undone.`)) return
    
    setDeleting(sketch.id)
    try {
      const response = await fetch(`/api/user/sketches?id=${sketch.id}&userId=${user.id}`, {
        method: 'DELETE'
      })
      
      const data = await response.json()
      
      if (data.success) {
        setSketches(prev => prev.filter(s => s.id !== sketch.id))
        showToast('Sketch deleted', 'success')
      } else {
        throw new Error(data.error)
      }
    } catch (error) {
      console.error('[MySketchesPanel] Error deleting:', error)
      showToast('Failed to delete sketch', 'error')
    } finally {
      setDeleting(null)
    }
  }
  
  const formatDate = (dateStr) => {
    const date = new Date(dateStr)
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    })
  }
  
  if (!isSignedIn) {
    return (
      <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
        <div className="bg-ide-surface border border-ide-border rounded-xl max-w-md w-full p-6 text-center">
          <FolderOpen className="mx-auto text-ide-textMuted mb-4" size={48} />
          <h2 className="text-lg font-semibold text-ide-text mb-2">Sign In Required</h2>
          <p className="text-ide-textMuted text-sm mb-4">
            Sign in to save and manage your sketches.
          </p>
          <button
            onClick={onClose}
            className="px-4 py-2 bg-ide-accent hover:bg-ide-accentHover text-white rounded-lg transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    )
  }
  
  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-fade-in">
      <div className="bg-ide-surface border border-ide-border rounded-xl max-w-2xl w-full max-h-[80vh] flex flex-col shadow-modal animate-scale-in">
        
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-ide-border">
          <div className="flex items-center gap-2">
            <FolderOpen className="text-ide-accent" size={20} />
            <h2 className="text-lg font-semibold text-ide-text">My Sketches</h2>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleSaveCurrentSketch}
              disabled={saving}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-ide-accent hover:bg-ide-accentHover text-white rounded-lg text-sm transition-colors disabled:opacity-50"
            >
              {saving ? (
                <Loader size={14} className="animate-spin" />
              ) : (
                <Plus size={14} />
              )}
              Save Current
            </button>
            <button
              onClick={onClose}
              className="text-ide-textMuted hover:text-ide-text transition-colors"
            >
              <X size={20} />
            </button>
          </div>
        </div>
        
        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader className="animate-spin text-ide-textMuted" size={32} />
            </div>
          ) : sketches.length === 0 ? (
            <div className="text-center py-12">
              <FolderOpen size={48} className="mx-auto mb-4 text-ide-textDim" />
              <p className="text-ide-textMuted mb-2">No saved sketches yet</p>
              <p className="text-ide-textDim text-sm">
                Click "Save Current" to save your first sketch!
              </p>
            </div>
          ) : (
            <div className="grid gap-3">
              {sketches.map(sketch => (
                <div
                  key={sketch.id}
                  className="group bg-ide-bg hover:bg-ide-surfaceHover border border-ide-border rounded-lg p-4 transition-all"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <h3 className="font-medium text-ide-text truncate group-hover:text-white transition-colors">
                        {sketch.name}
                      </h3>
                      {sketch.description && (
                        <p className="text-xs text-ide-textMuted mt-1 line-clamp-2">
                          {sketch.description}
                        </p>
                      )}
                      <div className="flex items-center gap-3 mt-2 text-xs text-ide-textDim">
                        <span className="flex items-center gap-1">
                          <Clock size={12} />
                          {formatDate(sketch.updatedAt)}
                        </span>
                        <span>p5.js v{sketch.p5Version}</span>
                        {sketch.isPublic && (
                          <span className="flex items-center gap-1 text-green-400">
                            <Eye size={12} />
                            Public
                          </span>
                        )}
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => handleLoadSketch(sketch)}
                        className="p-2 text-ide-textMuted hover:text-ide-accent hover:bg-ide-accent/10 rounded-lg transition-colors"
                        title="Load sketch"
                      >
                        <Upload size={16} />
                      </button>
                      <button
                        onClick={() => handleDeleteSketch(sketch)}
                        disabled={deleting === sketch.id}
                        className="p-2 text-ide-textMuted hover:text-red-400 hover:bg-red-400/10 rounded-lg transition-colors disabled:opacity-50"
                        title="Delete sketch"
                      >
                        {deleting === sketch.id ? (
                          <Loader size={16} className="animate-spin" />
                        ) : (
                          <Trash2 size={16} />
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        
        {/* Footer */}
        <div className="p-4 border-t border-ide-border bg-ide-bg/50">
          <p className="text-xs text-ide-textDim text-center">
            Sketches are stored securely in your account. You can also sync to editor.p5js.org.
          </p>
        </div>
      </div>
    </div>
  )
}
